import moment from 'moment';
import { UserData, Network, ReportDataResponse, ProductCodesResponse, MetricSettings } from './types';

/**
 * Generate dynamic period strings for current month and 3 months back
 */
export function getDemoPeriods(): string[] {
    const periods: string[] = [];
    for (let i = 0; i < 4; i++) {
        const period = moment().subtract(i, 'months').format('YYYY-MM');
        periods.push(period);
    }
    return periods.reverse(); // oldest first
}

/**
 * Mock user data for demo mode
 */
export function getDemoUserData(): UserData {
    return {
        user_valid: true,
        user: {
            id: 1,
            email: 'demo@example.com',
            first_name: 'Demo',
            last_name: 'User',
            role: 'network_manager',
            company_id: 0,
            companies: [
                {
                    id: 0,
                    name: 'Demo Company',
                    email: 'demo@example.com',
                    role: 'network_manager',
                    iban: 'DE89370400440532013000',
                    swift: 'COBADEFFXXX',
                    region: 'SK',
                    language: 'en',
                    currency: 'EUR',
                    slug: 'demo-company'
                }
            ],
            permissions: [
                {
                    permission: 'manage_network',
                    name: 'Manage Network',
                    description: 'Can manage network settings',
                    user_can: true
                },
                {
                    permission: 'get_users',
                    name: 'Get Users',
                    description: 'Can view users',
                    user_can: true
                },
                {
                    permission: 'get_company',
                    name: 'Get Company',
                    description: 'Can view company',
                    user_can: true
                }
            ]
        },
        company: {
            id: 0,
            name: 'Demo Company',
            email: 'demo@example.com',
            role: 'network_manager',
            iban: 'DE89370400440532013000',
            swift: 'COBADEFFXXX',
            region: 'SK',
            language: 'en',
            currency: 'EUR',
            slug: 'demo-company'
        },
        app: {
            allow_cancellation_after_limit: 1,
            application_id: 1,
            cancellation_after_limit_info: null,
            cancellation_limit: '24',
            cancellation_limit_active: 1,
            company_email: 'demo@example.com',
            company_id: 0,
            company_name: 'Demo Company',
            company_tnc: '',
            type: 'franchise'
        },
        agreements: [],
        branding: '',
        msg: {},
        widgets: []
    };
}

/**
 * Mock network data for demo mode
 */
export function getDemoNetwork(): Network {
    return {
        id: 1,
        downstream: [1, 2, 3, 4, 5],
        companies: [
            { id: 1, name: 'FitZone Bratislava' },
            { id: 2, name: 'Active Life Vienna' },
            { id: 3, name: 'HealthHub Prague' },
            { id: 4, name: 'PowerGym Budapest' },
            { id: 5, name: 'WellnessFirst Warsaw' }
        ]
    };
}

/**
 * Mock report data with dynamic dates for current month and 3 months back
 */
export function getDemoReportData(): ReportDataResponse {
    const periods = getDemoPeriods();
    const companies = getDemoNetwork().companies;

    const data = periods.flatMap(period => {
        return companies.map((company, index) => {
            // Generate random but consistent data based on period and company
            const baseRevenue = 15000 + (index * 5000);
            const monthOffset = periods.indexOf(period);
            const seasonalFactor = 1 + (Math.sin(monthOffset * Math.PI / 6) * 0.2);

            return {
                company_id: company.id,
                period: period,
                place_id: company.id,
                company_name: company.name,
                place_name: null,
                place_city: null,
                place_region: null,
                received_payments: (baseRevenue * seasonalFactor * 0.95).toFixed(2),
                new_enrollments: Math.floor(20 + (index * 5) + (monthOffset * 2)),
                ended: Math.floor(5 + (index * 2)),
                net_revenue: (baseRevenue * seasonalFactor).toFixed(2),
                revenue: (baseRevenue * seasonalFactor * 10).toFixed(2),
                enrollments: Math.floor(80 + (index * 10) + (monthOffset * 3)),
                cancellations: Math.floor(3 + index),
                royalties: (baseRevenue * seasonalFactor * 0.08).toFixed(2),
                sessions: Math.floor(40 + (index * 8)),
                unpaid_debt: (baseRevenue * 0.05).toFixed(2),
                unpaid_enrollments: Math.floor(2 + index),
                instructors: 3 + index,
                sessions_with_attendance: Math.floor(38 + (index * 8))
            };
        });
    });

    return {
        view: 'network',
        currency: 'EUR',
        meta: {
            lead_metric: 'revenue'
        },
        data
    };
}

/**
 * Mock places data - each company has 2-3 locations
 */
export function getDemoPlacesData(): ReportDataResponse {
    const periods = getDemoPeriods();
    const companies = getDemoNetwork().companies;

    // Define places for each company
    const placesByCompany = [
        // Company 1 - 3 locations
        [
            { name: 'Downtown Center', city: 'Bratislava', region: 'SK' },
            { name: 'Mall Plaza', city: 'Bratislava', region: 'SK' },
            { name: 'North District', city: 'Bratislava', region: 'SK' }
        ],
        // Company 2 - 2 locations
        [
            { name: 'City Center', city: 'Vienna', region: 'AT' },
            { name: 'Shopping Mall', city: 'Vienna', region: 'AT' }
        ],
        // Company 3 - 3 locations
        [
            { name: 'Main Street', city: 'Prague', region: 'CZ' },
            { name: 'Old Town', city: 'Prague', region: 'CZ' },
            { name: 'Business District', city: 'Prague', region: 'CZ' }
        ],
        // Company 4 - 2 locations
        [
            { name: 'Central Plaza', city: 'Budapest', region: 'HU' },
            { name: 'Riverside', city: 'Budapest', region: 'HU' }
        ],
        // Company 5 - 2 locations
        [
            { name: 'Downtown', city: 'Warsaw', region: 'PL' },
            { name: 'West End', city: 'Warsaw', region: 'PL' }
        ]
    ];


    const data = periods.flatMap(period => {
        const monthOffset = periods.indexOf(period);

        return companies.flatMap((company, companyIndex) => {
            const places = placesByCompany[companyIndex] || [];

            return places.map((place, placeIndex) => {
                // Each place gets a portion of the company's revenue
                const baseRevenue = (8000 + (companyIndex * 3000) + (placeIndex * 2000));
                const seasonalFactor = 1 + (Math.sin(monthOffset * Math.PI / 6) * 0.2);
                const placeId = (companyIndex * 10) + placeIndex + 1;

                return {
                    company_id: company.id,
                    period: period,
                    place_id: placeId,
                    company_name: company.name,
                    place_name: place.name,
                    place_city: place.city,
                    place_region: place.region,
                    received_payments: (baseRevenue * seasonalFactor * 0.95).toFixed(2),
                    new_enrollments: Math.floor(12 + (placeIndex * 3) + (monthOffset * 2)),
                    ended: Math.floor(3 + placeIndex),
                    net_revenue: (baseRevenue * seasonalFactor).toFixed(2),
                    revenue: (baseRevenue * seasonalFactor * 10).toFixed(2),
                    enrollments: Math.floor(45 + (placeIndex * 8) + (monthOffset * 2)),
                    cancellations: Math.floor(2 + placeIndex),
                    royalties: (baseRevenue * seasonalFactor * 0.08).toFixed(2),
                    sessions: Math.floor(25 + (placeIndex * 5)),
                    unpaid_debt: (baseRevenue * 0.05).toFixed(2),
                    unpaid_enrollments: Math.floor(1 + placeIndex),
                    instructors: 2 + Math.floor(placeIndex / 2),
                    sessions_with_attendance: Math.floor(23 + (placeIndex * 5))
                };
            });
        });
    });

    return {
        view: 'network',
        currency: 'EUR',
        meta: {
            lead_metric: 'revenue'
        },
        data
    };
}

/**
 * Mock product codes for demo mode
 */
export function getDemoProductCodes(): ProductCodesResponse {
    return {
        total: 5,
        page: 1,
        page_size: 50,
        settings: [],
        data: [
            {
                id: 1,
                network_id: 1,
                name: 'Yoga Classes',
                code: 'YOGA',
                created: moment().subtract(1, 'year').format('YYYY-MM-DD HH:mm:ss'),
                updated: moment().subtract(1, 'month').format('YYYY-MM-DD HH:mm:ss')
            },
            {
                id: 2,
                network_id: 1,
                name: 'Pilates Classes',
                code: 'PILATES',
                created: moment().subtract(1, 'year').format('YYYY-MM-DD HH:mm:ss'),
                updated: moment().subtract(2, 'months').format('YYYY-MM-DD HH:mm:ss')
            },
            {
                id: 3,
                network_id: 1,
                name: 'Fitness Training',
                code: 'FITNESS',
                created: moment().subtract(1, 'year').format('YYYY-MM-DD HH:mm:ss'),
                updated: moment().subtract(3, 'months').format('YYYY-MM-DD HH:mm:ss')
            },
            {
                id: 4,
                network_id: 1,
                name: 'Dance Classes',
                code: 'DANCE',
                created: moment().subtract(1, 'year').format('YYYY-MM-DD HH:mm:ss'),
                updated: moment().subtract(1, 'month').format('YYYY-MM-DD HH:mm:ss')
            },
            {
                id: 5,
                network_id: 1,
                name: 'Martial Arts',
                code: 'MARTIAL',
                created: moment().subtract(1, 'year').format('YYYY-MM-DD HH:mm:ss'),
                updated: moment().subtract(2, 'months').format('YYYY-MM-DD HH:mm:ss')
            }
        ]
    };
}

/**
 * Mock metric settings for demo mode
 */
export function getDemoMetricSettings(): MetricSettings {
    return {
        metrics: [
            { metric: 'revenue' },
            { metric: 'net_revenue' },
            { metric: 'received_payments' },
            { metric: 'enrollments' },
            { metric: 'new_enrollments' },
            { metric: 'cancellations' },
            { metric: 'royalties' },
            { metric: 'sessions' },
            { metric: 'instructors' }
        ],
        lead_metric: 'revenue'
    };
}

/**
 * Generate mock data for product-specific reports
 */
export function getDemoProductReportData(productCode?: string): ReportDataResponse {
    const periods = getDemoPeriods();
    const companies = getDemoNetwork().companies;

    const data = periods.flatMap(period => {
        return companies.map((company, index) => {
            const baseRevenue = 8000 + (index * 2000);
            const monthOffset = periods.indexOf(period);
            const seasonalFactor = 1 + (Math.sin(monthOffset * Math.PI / 6) * 0.15);

            return {
                company_id: company.id,
                period: period,
                place_id: company.id,
                company_name: company.name,
                place_name: null,
                place_city: null,
                place_region: null,
                received_payments: (baseRevenue * seasonalFactor * 0.95).toFixed(2),
                new_enrollments: Math.floor(10 + (index * 2) + monthOffset),
                ended: Math.floor(2 + index),
                net_revenue: (baseRevenue * seasonalFactor).toFixed(2),
                revenue: (baseRevenue * seasonalFactor * 10).toFixed(2),
                enrollments: Math.floor(40 + (index * 5) + monthOffset),
                cancellations: Math.floor(1 + index),
                royalties: (baseRevenue * seasonalFactor * 0.08).toFixed(2),
                sessions: Math.floor(20 + (index * 4)),
                unpaid_debt: (baseRevenue * 0.05).toFixed(2),
                unpaid_enrollments: Math.floor(1 + index),
                instructors: 2 + Math.floor(index / 2),
                sessions_with_attendance: Math.floor(18 + (index * 4))
            };
        });
    });

    return {
        view: productCode || 'all_products',
        currency: 'EUR',
        meta: {
            lead_metric: 'revenue'
        },
        data
    };
}

/**
 * Generate mock data for company-specific reports
 */
export function getDemoCompanyReportData(companyId: number): ReportDataResponse {
    const periods = getDemoPeriods();
    const company = getDemoNetwork().companies.find(c => c.id === companyId) || getDemoNetwork().companies[0];

    const data = periods.map(period => {
        const monthOffset = periods.indexOf(period);
        const baseRevenue = 18000;
        const seasonalFactor = 1 + (Math.sin(monthOffset * Math.PI / 6) * 0.2);

        return {
            company_id: company.id,
            period: period,
            place_id: company.id,
            company_name: company.name,
            place_name: null,
            place_city: null,
            place_region: null,
            received_payments: (baseRevenue * seasonalFactor * 0.95).toFixed(2),
            new_enrollments: Math.floor(25 + monthOffset * 3),
            ended: Math.floor(8 + monthOffset),
            net_revenue: (baseRevenue * seasonalFactor).toFixed(2),
            revenue: (baseRevenue * seasonalFactor * 10).toFixed(2),
            enrollments: Math.floor(100 + monthOffset * 5),
            cancellations: Math.floor(5 + monthOffset),
            royalties: (baseRevenue * seasonalFactor * 0.08).toFixed(2),
            sessions: Math.floor(50 + monthOffset * 2),
            unpaid_debt: (baseRevenue * 0.05).toFixed(2),
            unpaid_enrollments: Math.floor(3 + monthOffset),
            instructors: 4,
            sessions_with_attendance: Math.floor(48 + monthOffset * 2)
        };
    });

    return {
        view: 'company',
        currency: 'EUR',
        meta: {
            lead_metric: 'revenue'
        },
        data
    };
}

/**
 * Generate mock data for metric_product view (time-series format)
 */
export function getDemoMetricProductData(): any {
    const periods = getDemoPeriods();
    const products = getDemoProductCodes().data;

    const data = products.map(product => {
        const metricsData: any = {
            product_code_id: product.code,
            company_id: 0
        };

        // Create time-series data for each metric
        const metrics = [
            'net_revenue',
            'revenue',
            'received_payments',
            'enrollments',
            'new_enrollments',
            'cancellations',
            'royalties',
            'ended',
            'unpaid_debt',
            'unpaid_enrollments',
            'instructors',
            'sessions_with_attendance'
        ];

        metrics.forEach(metric => {
            metricsData[metric] = periods.map((period, monthOffset) => {
                const baseValue = getMetricBaseValue(metric, product.id);
                const seasonalFactor = 1 + (Math.sin(monthOffset * Math.PI / 6) * 0.15);

                return {
                    period: period,
                    metric_value: (baseValue * seasonalFactor).toFixed(metric.includes('revenue') || metric.includes('payment') || metric.includes('royalties') || metric.includes('debt') ? 2 : 0)
                };
            });
        });

        return metricsData;
    });

    return {
        view: 'metric_product',
        currency: 'EUR',
        meta: {
            lead_metric: 'revenue'
        },
        data
    };
}

/**
 * Helper function to get base value for a metric
 */
function getMetricBaseValue(metric: string, productId: number): number {
    const productMultiplier = 1 + (productId * 0.3);

    switch (metric) {
        case 'net_revenue':
            return 8000 * productMultiplier;
      case 'revenue':
        return 80000 * productMultiplier;
        case 'received_payments':
            return 7600 * productMultiplier;
        case 'enrollments':
            return 60 * productMultiplier;
        case 'new_enrollments':
            return 15 * productMultiplier;
        case 'cancellations':
            return 3 * productMultiplier;
        case 'royalties':
            return 640 * productMultiplier;
        case 'ended':
            return 5 * productMultiplier;
        case 'unpaid_debt':
            return 400 * productMultiplier;
        case 'unpaid_enrollments':
            return 2 * productMultiplier;
        case 'instructors':
            return 3;
        case 'sessions_with_attendance':
            return 25 * productMultiplier;
        default:
            return 100 * productMultiplier;
    }
}
